import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import { getPageFromList, TestSentence } from "@/model";
import { usePageParam } from "@/hook";
import PaginationBar from "@/component/PaginationBar";
import SortButton from "@/component/SortBurtton";


const TEST_SENTENCE_PAGE_SIZE = 15;

interface SentenceResultTableProps {
    successes: TestSentence[];
    fails: TestSentence[];
}

const SentenceResultTable: React.FC<SentenceResultTableProps> = ({ successes, fails }) => {
    const intl = useIntl();

    const { param, set: setParam } = usePageParam<{
        page: number;
        sortBy: "result" | "score" | undefined;
        sortOrder: "asc" | "desc";
    }>({
        page: 0,
        sortBy: undefined,
        sortOrder: "desc"
    });

    const onClickSort = (sortBy: "result" | "score") => {
        if (param?.sortBy === sortBy) {
            setParam({ sortOrder: param?.sortOrder === "asc" ? "desc" : "asc" });
        } else {
            setParam({ sortBy });
        }
    };

    const mergedSentences = useMemo(() => {
        const result: TestSentence[] = [];
        let successIndex = 0, failIndex = 0;
        let successCur = successes[successIndex], failCur = fails[failIndex];

        while (successCur && failCur) {
            if (successCur.created_at < failCur.created_at) {
                result.push(successCur);
                successIndex += 1;
                successCur = successes[successIndex];
            } else {
                result.push(failCur);
                failIndex += 1;
                failCur = fails[failIndex];
            }
        };
        return result.concat(successes.slice(successIndex), fails.slice(failIndex));
    }, [successes, fails]);

    const sortedSentences = useMemo<TestSentence[]>(() => {
        const sortBy = param?.sortBy;
        const order = param?.sortOrder;

        if (!sortBy) {
            return [...mergedSentences];
        }
        if (sortBy === "result") {
            return order === "asc" ? [...fails, ...successes] : [...successes, ...fails];
        } else {
            return [...mergedSentences].sort((a, b) => {
                const diff = a.score - b.score;
                if (diff !== 0) {
                    return order === "asc" ? diff : -diff;
                }
                return a.created_at > b.created_at ? -1 : 1;
            });
        }
    }, [mergedSentences, successes, fails, param?.sortBy, param?.sortOrder]);

    const sentencesPage = useMemo(() =>
        getPageFromList<TestSentence>(sortedSentences, param?.page, TEST_SENTENCE_PAGE_SIZE),
    [sortedSentences, param?.page]);

    return (
        <div className="table-pagination-container">
            <div role="table"
                 className="selection-table grid-table-bulk-test-sentence-result"
            >
                <div role="rowgroup"
                     className="selection-table-thead"
                >
                    <div role="columnheader"
                         className="selection-table-th pl-[25px]"
                    >
                        {intl.formatMessage({ id: "i09027" })}
                    </div>
                    <div role="columnheader"
                         className="selection-table-th pl-[15px]"
                    >
                        {intl.formatMessage({ id: "i09029" })}
                    </div>
                    <div role="columnheader"
                         className="selection-table-th pl-[15px]"
                    >
                        {intl.formatMessage({ id: "i09031" })}
                    </div>
                    <div role="columnheader"
                         className="selection-table-th group pl-[15px]"
                    >
                        <SortButton order={param?.sortOrder}
                                    active={param?.sortBy === "result"}    
                                    onClick={() => onClickSort("result")}
                        >
                            {intl.formatMessage({ id: "i09033" })}
                        </SortButton>
                    </div>
                    <div role="columnheader"
                         className="selection-table-th group px-[15px]"
                    >
                        <SortButton order={param?.sortOrder}
                                    active={param?.sortBy === "score"}    
                                    onClick={() => onClickSort("score")}
                        >
                            {intl.formatMessage({ id: "i09037" })}
                        </SortButton>
                    </div>
                </div>
                <div role="rowgroup"
                     className="selection-table-tbody max-h-[540px]"
                >
                    {sentencesPage.content.map((sentence, index) => (
                        <div key={sentence.id}
                             role="row"
                             className="selection-table-tr"
                        >
                            <div role="cell"
                                 className="selection-table-td px-[25px] tabular-nums"
                            >
                                <span className="mr-2 break-keep">
                                    {index + 1 + (sentencesPage.page * TEST_SENTENCE_PAGE_SIZE)}
                                </span>
                                {sentence.query}
                            </div>
                            <div role="cell"
                                 className="selection-table-td px-[15px]"
                            >
                                {sentence.expected_intent_name || "-"}
                            </div>
                            <div role="cell"
                                 className="selection-table-td px-[15px]"
                            >
                                {sentence.result_intent_name || "-"}
                            </div>
                            <div role="cell"
                                 className="selection-table-td px-[15px]"
                            >
                                {intl.formatMessage({ id: sentence.correct ? "i09035" : "i09036" })}
                            </div>
                            <div role="cell"
                                 className="selection-table-td px-[15px] tabular-nums"
                            >
                                {sentence.score.toFixed(2)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <PaginationBar pageData={sentencesPage}
                            onClickPage={page => setParam({ page: page })}
            />
        </div>
    );
};

export default SentenceResultTable;
